import { createContext, useContext, useEffect, useMemo } from 'react';
import Preloader from "~/components/Shared/Preloader";
import { getAuthUser } from "~/store/actions";
import { NetworkErrorException } from '~/helpers/errorHelper';
import Error from '~/pages/Error';
import { useAppDispatch, useAppSelector } from '~/hooks';

type AuthContextType = {
  user: any;
  isLoggedIn: () => boolean;
  isImpersonation: () => boolean;
  refreshAuthUser: () => void;
};

const AuthContext = createContext<AuthContextType | null>(null);

type AuthProviderProps = {
  children: React.ReactNode;
};

const AuthProvider = ({ children }: AuthProviderProps) => {
  const dispatch = useAppDispatch();

  // warning
  // useSelector() will cause a re-render whenever the result of its callback function changes
  // for example: const { user, isAuth } = useSelector(state => state.Auth.Login);
  // you might expect to get a re-render only when 'user' or 'isAuth' change
  // in fact you will get a re-render when anything inside 'state.Auth.Login' changes, not just 'user' and 'isAuth'
  // so if that object contains other properties, unwanted re-renders will happen when they change
  // that is why, instead of destructuring, we target specific state properties one by one
  // https://9oelm.github.io/2020-09-13--How-to-make-useSelector-not-a-disaster/#function-component
  const user = useAppSelector(state => state.Auth.Login.user);
  const isAuth = useAppSelector(state => state.Auth.Login.isAuth);
  const userError = useAppSelector(state => state.Auth.Login.userError);

  const isLoggedIn = () => !!user;

  const isImpersonation = () => user.isImpersonated;

  useEffect(() => {
    if (isAuth === null) {
      refreshAuthUser();
    }
  }, [isAuth]);

  const refreshAuthUser = () => dispatch(getAuthUser());

  const value = useMemo(
    () => ({ user, isLoggedIn, isImpersonation, refreshAuthUser }),
    [user] // isLoggedIn/isImpersonation/refreshAuthUser only close over user and dispatch (stable)
  );

  // wait until we decide whether the user is authenticated or not
  if (isAuth !== null) {
    if (userError instanceof NetworkErrorException) {
      return <Error error={userError} />
    }
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  }
  // until then show a preloader
  return <Preloader />
}

// helper hook that makes context data available
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthProvider;